import {
  ApolloClient,
  ApolloLink,
  from,
  HttpLink,
  InMemoryCache,
  Observable,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';

import { getPlatform } from '../../../capacitorWrapper';

import Config from '@/config';
import debug from '@/util/debug';
import TokenManager from '@/util/tokenManager';

export const AUTH_HEADER = 'authorization';
export const CLIENT_HEADER = 'x-client';
export const CLIENT_VERSION_HEADER = 'x-client-version';
export const NEW_TOKEN_HEADER = 'x-refreshed-token';
export const REFRESH_HEADER = 'x-refresh';
export const REQUEST_ID_HEADER = 'x-request-id';
export const APP_PLATFORM_HEADER = 'x-app-platform';

const clientVersion = `${Config.BUILD_VERSION}-${Config.BUILD_COMMIT}`;

function getTokenManager() {
  return TokenManager.getInstance();
}

// ==
// HTTP config
// ==
const httpLink = new HttpLink({
  uri: Config.GRAPHQL_ENDPOINT,
});

// ==
// Authentication
// ==
const authLink = new ApolloLink((operation, forward) => {
  const customHeaders: Record<string, string> = {
    [CLIENT_HEADER]: 'area2k-app',
    [CLIENT_VERSION_HEADER]: clientVersion,
  };

  const accessToken = getTokenManager().getAccessToken();
  const deviceToken = getTokenManager().getDeviceToken();

  if (accessToken) customHeaders[AUTH_HEADER] = accessToken;
  if (deviceToken) customHeaders[REFRESH_HEADER] = deviceToken;

  const currentPlatform = getPlatform();

  if (currentPlatform) {
    customHeaders[APP_PLATFORM_HEADER] = currentPlatform;
  }

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      ...customHeaders,
    },
  }));

  return forward(operation);
});

const tokenRefreshLink = new ApolloLink((operation, forward) =>
  forward(operation).map((response) => {
    const {
      response: { headers },
    } = operation.getContext();
    const newToken = headers.get(NEW_TOKEN_HEADER);

    if (newToken) getTokenManager().handleNewToken(newToken);

    return response;
  }),
);

// ==
// GraphQL auth error handling (200 + AUTHORIZATION_FAILED)
// ==
const authErrorLink = onError(({ graphQLErrors, operation, forward }) => {
  if (!graphQLErrors) return;

  const authError = graphQLErrors.find(
    (err) =>
      err.extensions?.code === 'AUTHORIZATION_FAILED' ||
      err.extensions?.code === 'UNAUTHORIZED',
  );

  if (!authError) return;

  const context = operation.getContext();
  if (context._authRetried) return;

  return new Observable((observer) => {
    getTokenManager()
      .ensureFreshToken()
      .then((newToken) => {
        if (!newToken) {
          observer.error(authError);
          return;
        }

        operation.setContext(({ headers = {} }) => ({
          headers: {
            ...headers,
            [AUTH_HEADER]: newToken,
          },
          _authRetried: true,
        }));

        forward(operation).subscribe(observer);
      })
      .catch((err) => {
        observer.error(err);
      });
  });
});

// ==
// Network error handling (401 retry)
// ==
const serverErrorLink = onError(({ networkError, operation, forward }) => {
  if (!networkError) return;

  const statusCode = (networkError as { statusCode?: number }).statusCode;

  if (statusCode === 401) {
    const context = operation.getContext();
    if (context._authRetried) {
      console.error(
        '[apollo]',
        '(serverErrorLink)',
        'retry failed, 401 persisted',
      );
      return;
    }

    return new Observable((observer) => {
      getTokenManager()
        .ensureFreshToken()
        .then((newToken) => {
          if (!newToken) {
            observer.error(networkError);
            return;
          }

          operation.setContext(({ headers = {} }) => ({
            headers: {
              ...headers,
              [AUTH_HEADER]: newToken,
            },
            _authRetried: true,
          }));

          forward(operation).subscribe(observer);
        })
        .catch((err) => {
          observer.error(err);
        });
    });
  }

  console.error('[apollo]', '(serverErrorLink)', networkError);
});

// ==
// Debug logging
// ==
const loggerLink = new ApolloLink((operation, forward) =>
  forward(operation).map((response) => {
    const context = operation.getContext();
    const requestId = context.response.headers.get(REQUEST_ID_HEADER);

    debug.log('[apollo]', `(${requestId})`, response);

    return response;
  }),
);

// ==
// Composition
// ==

const httpLinkWithMiddleware = loggerLink.concat(
  serverErrorLink.concat(tokenRefreshLink.concat(httpLink)),
);

export default new ApolloClient({
  cache: new InMemoryCache({}),
  link: from([authLink, authErrorLink, httpLinkWithMiddleware]),
  resolvers: {},
});
